import * as React from 'react';
import { t } from '~i18n';
import type { Choice } from '~components/Choice';
import { DropDownOption, Field, FieldValue, isOptionObject } from '../schema';
import { appliesToDeviceType } from '../utils/gating';

export const optionValue = (option: DropDownOption): FieldValue => (isOptionObject(option) ? option.value : option);

export interface ChoiceFieldProps {
  field: Field;
  value: FieldValue;
  /** Absent offers every option; see `appliesToDeviceType`. */
  deviceType?: string;
  onChange: (id: string, value: FieldValue) => void;
  /** Renders the fields an option reveals, inside that option. */
  renderRevealed?: (ids: string[]) => React.ReactNode;
}

export interface ChoiceField {
  /** The options this device type is offered, in declaration order. */
  offered: DropDownOption[];
  /** Ready for `ChoiceList`, labels resolved and revealed content attached. */
  choices: Choice[];
  /** What the radio group shows as selected. */
  selected: string;
  /** Whether the checkbox reads as on, for a field that has one. */
  checked: boolean;
  /** Picks a choice, recovering the typed value the radio group gave back as a string. */
  choose: (selected: string) => void;
  /** Ticks or unticks the checkbox over these choices. */
  setChecked: (checked: boolean) => void;
}

export const useChoiceField = ({
  field,
  value,
  deviceType,
  onChange,
  renderRevealed,
}: ChoiceFieldProps): ChoiceField => {
  const offered = React.useMemo(
    () => (field.options ?? []).filter((o) => !isOptionObject(o) || appliesToDeviceType(o.only_for, deviceType)),
    [field.options, deviceType]
  );

  const choices = React.useMemo(
    () =>
      offered.map((option): Choice => {
        const optionText = String(optionValue(option));
        if (!isOptionObject(option)) return { value: optionText, title: optionText };
        return {
          value: optionText,
          title: option.label_key ? t(option.label_key) : optionText,
          description: option.description_key ? t(option.description_key) : undefined,
          // Drawn under this option while it is the selected one -- a perimeter belongs to the
          // choice that needs one, not to the field as a whole.
          content: option.reveals?.length ? renderRevealed?.(option.reveals) : undefined,
        };
      }),
    [offered, renderRevealed]
  );

  const choose = (selected: string) => {
    const match = offered.find((o) => String(optionValue(o)) === selected);
    onChange(field.id, match === undefined ? selected : optionValue(match));
  };

  // Two shapes, by whether the field has choices of its own. A boolean's off state is `false`; a
  // field with choices stores `off_value` instead, and its choices render inside the box.
  const checked = field.off_value !== undefined ? value !== field.off_value : value === true;

  const setChecked = (next: boolean) => {
    if (!next) return onChange(field.id, field.off_value ?? false);
    if (field.off_value === undefined) return onChange(field.id, true);
    // The first offered choice, so turning the alarm on leaves something chosen rather than a list
    // with nothing selected and a value the save would refuse.
    const first = offered[0];
    onChange(field.id, first === undefined ? true : optionValue(first));
  };

  return {
    offered,
    choices,
    selected: value == null ? '' : String(value),
    checked,
    choose,
    setChecked,
  };
};
